import { SolidContainer } from "@ldo/connected-solid";
import { useSolidAuth } from "@ldo/solid-react";
import { parseRdf } from "@ldo/ldo";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SolidProfileShapeType } from "../.ldo/solidProfile.shapeTypes";
import { useAppContext } from "./AppContext";
import { makeNamedNode } from "../lib/nodes";
import { rdf, rdfs, spoty } from "../lib/ns";
import { cap } from "../lib/cap";
import { Modal } from "../components/Modal"
// CSS
import "./ImportInboxContent.css";

export const ImportInbox: FunctionComponent<{
    workspace: SolidContainer,
    setFileNumber: (n: number) => void,
    showModal: boolean,
    setShowModal: (value: boolean) => void
}> = ({
    workspace,
    setFileNumber,
    showModal,
    setShowModal
}) => {
        const { fetch: authFetch } = useSolidAuth();
        const { t } = useTranslation("translation");
        const [appContext] = useAppContext();

        const [inboxFiles, setInboxFiles] = useState<string[]>([]);
        const [importStatus, setImportStatus] = useState<string>("");
        const [inboxToSourceMap, setInboxToSourceMap] = useState<Map<string, string>>(new Map());

        useEffect(() => {
            if (inboxFiles) {
                setFileNumber(inboxFiles.length);
            }
        }, [inboxFiles, setFileNumber]);

        const loadInbox = useCallback(async () => {
            if (appContext.inboxUrl, { cache: "no-store" }) {
                // force refresh of inbox
                fetch(appContext.inboxUrl, { cache: "no-cache" });

                const result = await discoverCollections({
                    inboxUrl: appContext.inboxUrl,
                    setImportStatus,
                    authFetch,
                    t
                });
                if (result && typeof result === "object" && "inboxToSourceMap" in result) {
                    setInboxFiles(result.allSeeAlsoUris);
                    setInboxToSourceMap(result.inboxToSourceMap);
                    setFileNumber(result.allSeeAlsoUris.length);
                }
            }
        }, [appContext.inboxUrl, authFetch, t, setFileNumber]);

        useEffect(() => {
            if (!appContext.inboxUrl) return;

            const interval = setInterval(() => {
                loadInbox();
            }, 15000);

            return () => clearInterval(interval);
        }, [appContext.inboxUrl, loadInbox]);

        const handleImport = async (selectedUris: string[]) => {
            setShowModal(false);
            setImportStatus(t("Import in progress..."));

            await importSentences({
                uris: selectedUris,
                authFetch,
                workspaceUri: workspace.uri,
                inboxToSourceMap,
                setImportStatus,
                t
            });

            loadInbox();
            await workspace.read();
            setImportStatus("");
        };

        return <>
            {showModal && (
                <ChooseContentToImport inboxContent={inboxFiles} show={showModal} setShow={() => setShowModal(false)} onConfirm={handleImport} />
            )}
            <p>{importStatus}</p>
        </>
    }

// To discover the inbox from a webID
export const discoverInbox = async (webID: string) => {
    const iri = webID.split('#')[0];

    const resp = await fetch(iri, {
        headers: { accept: "text/turtle" },
    });

    const turtle = await resp.text();

    const dataset = await parseRdf(turtle, {
        baseIRI: iri,
    });

    const profile = dataset
        .usingType(SolidProfileShapeType)
        .fromSubject(webID);

    if (!profile.inbox) {
        console.error("No ldp:inbox found");
        return null;
    }
    return profile.inbox?.["@id"];
}

// To dowload the inbox content
export const discoverCollections = async ({
    inboxUrl,
    setImportStatus,
    authFetch,
    t,
}: {
    inboxUrl: string,
    setImportStatus: (msg: string) => void,
    authFetch: typeof fetch,
    t: (key: string) => string,
}) => {
    const allSeeAlsoUris: string[] = [];
    const inboxToSourceMap = new Map<string, string>();

    if (!inboxUrl) {
        console.error("Inbox URL not found.");
        return { allSeeAlsoUris, inboxToSourceMap };
    }

    try {
        const resp = await authFetch(inboxUrl, {
            headers: { Accept: "text/turtle" },
            credentials: "include"
        });

        if (!resp.ok) {
            setImportStatus(t("Import failed"));
            setTimeout(() => {
                setImportStatus("");
            }, 3000);
            console.error(`Failed to fetch inbox: ${resp.statusText}`);
            return { allSeeAlsoUris, inboxToSourceMap };
        }

        const turtle = await resp.text();
        const quads = await parseRdf(turtle, {
            baseIRI: inboxUrl
        });

        const content = quads
            .match(makeNamedNode(inboxUrl), makeNamedNode("http://www.w3.org/ns/ldp#contains"), null)
            .toArray()
            .map(q => q.object.value);

        for (const uri of content) {
            try {
                const res = await authFetch(uri, {
                    headers: { Accept: "text/turtle" },
                    credentials: "include"
                });

                if (!res.ok) {
                    console.warn(`Skipping ${uri} (status: ${res.status})`);
                    continue;
                }

                const turtle = await res.text();
                const dataset = await parseRdf(turtle, { baseIRI: uri });

                const remoteCollections = dataset
                    .match(null, makeNamedNode(rdf.type), makeNamedNode(spoty.RemoteSentenceCollection))
                    .toArray();

                for (const collection of remoteCollections) {
                    const seeAlsos = dataset
                        .match(collection.subject, makeNamedNode(rdfs.seeAlso), null)
                        .toArray()
                        .map(q => q.object.value);

                    for (const sourceUri of seeAlsos) {
                        const uniqueKey = `${sourceUri}|${Date.now()}`;
                        inboxToSourceMap.set(uniqueKey, uri);
                    }

                    allSeeAlsoUris.push(...seeAlsos);
                }
            } catch (error) {
                console.error(`Error processing file ${uri}:`, error);
            }
        }
        return { allSeeAlsoUris, inboxToSourceMap };
    } catch (error) {
        console.error("Error in discoverCollections:", error);
        setImportStatus(t("Import failed"));
        setTimeout(() => {
            setImportStatus("");
        }, 3000);
    }
}

const importSentences = async ({
    uris,
    workspaceUri,
    inboxToSourceMap,
    setImportStatus,
    authFetch,
    t
}: {
    uris: string[],
    workspaceUri: string,
    inboxToSourceMap: Map<string, string>,
    setImportStatus: (msg: string) => void,
    authFetch: typeof fetch,
    t: (key: string) => string,
}) => {
    let importId = 1;

    for (const uriToImport of uris) {
        const label = uriToImport.split("/").pop() + "_imported";
        const uniqueLabel = `${label}_${importId++}`;

        const remoteSentenceCollectionContent = `<>
    <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> <https://w3id.org/SpOTy/ontology#RemoteSentenceCollection> ;
    <http://www.w3.org/2000/01/rdf-schema#seeAlso> <${uriToImport}> .`;

        try {
            const resp = await authFetch(`${workspaceUri}${uniqueLabel}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "text/turtle"
                },
                body: remoteSentenceCollectionContent,
                credentials: "include"
            });

            if (resp.ok) {
                const inboxFilesToDelete = [];

                for (const [key, inboxFileUri] of inboxToSourceMap.entries()) {
                    const [sourceUri] = key.split("|");
                    if (sourceUri === uriToImport) {
                        inboxFilesToDelete.push(inboxFileUri);
                    }
                }

                if (inboxFilesToDelete.length === 0) {
                    console.warn("No inbox file found to delete for:", uriToImport);
                    alert(t("An error occurred."));
                }

                for (const fileUri of inboxFilesToDelete) {
                    const deleteFile = await authFetch(fileUri, {
                        method: "DELETE",
                        credentials: "include"
                    });

                    if (!deleteFile.ok) {
                        console.error("Error deleting source file:", deleteFile.statusText);
                    }
                }
            } else {
                console.error("Error importing:", resp.status, resp.statusText);
                alert(t("An error occurred."));
            }
        } catch (e) {
            console.error("Error:", e);
        }
    }
}

const ChooseContentToImport: FunctionComponent<{
    inboxContent: string[] | null,
    show: boolean,
    setShow: () => void,
    onConfirm: (selectedUris: string[]) => void
}> = ({
    inboxContent,
    show,
    setShow,
    onConfirm
}) => {
        const { t } = useTranslation("translation");
        const [selectedUris, setSelectedUris] = useState<number[]>([]);
        const [allSelected, setAllSelected] = useState(false);

        if (!inboxContent) return null;

        const handleSelection = (index: number) => {
            setSelectedUris(prev =>
                prev.includes(index)
                    ? prev.filter(k => k !== index)
                    : [...prev, index]
            );
        };

        const importSelection = () => {
            const selected = selectedUris.map(i => inboxContent[i]);
            onConfirm(selected);
        }

        const toggleSelect = () => {
            if (allSelected) {
                setSelectedUris([]);
                setAllSelected(false);
            } else {
                const newSelection: number[] = [];
                for (let i = 0; i < inboxContent.length; i++) {
                    newSelection.push(i);
                }
                setSelectedUris(newSelection);
                setAllSelected(true);
            }
        };

        return (
            <Modal show={show} setShow={setShow}>
                <h2>{t("Select what you wish to import:")}</h2>
                <button id="selectAll" onClick={toggleSelect}>{allSelected ? t("Unselect all") : t("Select all")}</button>
                {inboxContent && inboxContent.length > 0
                    ? <> {inboxContent.map((uri, index) => (
                        <label key={index} id="inboxContent">
                            <input type="checkbox" checked={selectedUris.includes(index)} onChange={() => handleSelection(index)} />{uri}
                        </label>
                    ))} </>
                    : <p>{t("You have nothing in your inbox.")}</p>
                }
                <button id="importButton" onClick={importSelection}>{cap(t("validate"))}</button>
            </Modal>
        );
    }
